import { useWindowSize } from '@brands/hooks';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import styles from './styles.module.scss';

interface OHRReviewDialogProps {
  open: boolean;
  pdfUrl: string;
  onClose: () => void;
  onSend: () => Promise<void>;
}

const SCALE_MIN = 0.5;
const SCALE_MAX = 3.0;
const SCALE_STEP = 0.25;
const SWIPE_THRESHOLD = 50;
const DOUBLE_TAP_MS = 300;

interface ZoomToolbarProps {
  scale: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}

const ZoomToolbar: React.FC<ZoomToolbarProps> = ({ scale, onZoomIn, onZoomOut, onReset }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      background: '#f5f5f5',
      borderRadius: 999,
      px: 1.5,
      py: 0.25,
      border: '1px solid #e0e0e0',
    }}
  >
    <Tooltip title="Zoom out">
      <span>
        <IconButton size="small" onClick={onZoomOut} disabled={scale <= SCALE_MIN}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>

    <Tooltip title="Reset to 100%">
      <Typography
        variant="caption"
        role="button"
        tabIndex={0}
        onClick={onReset}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onReset()}
        sx={{
          minWidth: 38,
          textAlign: 'center',
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
          color: 'text.secondary',
          fontSize: '0.75rem',
          '&:hover': { color: 'text.primary' },
        }}
      >
        {Math.round(scale * 100)}%
      </Typography>
    </Tooltip>

    <Tooltip title="Zoom in">
      <span>
        <IconButton size="small" onClick={onZoomIn} disabled={scale >= SCALE_MAX}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

interface TouchState {
  type: 'single' | 'pinch';
  startX: number;
  startY: number;
  startDist: number;
  startScale: number;
  startPanX: number;
  startPanY: number;
  startCenterX?: number;
  startCenterY?: number;
}

const getTouchDistance = (touches: TouchList) => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;

  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchCenter = (touches: TouchList) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2,
});

const OHRReviewDialog: React.FC<OHRReviewDialogProps> = ({ open, pdfUrl, onClose, onSend }) => {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasReachedLastPage, setHasReachedLastPage] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [loadingSend, setLoadingSend] = useState(false);
  const [isHoveringPage, setIsHoveringPage] = useState(false);

  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768;

  const mobilePageWidth = useMemo(() => {
    if (!isMobile) return undefined;

    return Math.min(screenSize.width, window.innerWidth);
  }, [screenSize.width, isMobile]);

  const pageContainerRef = useRef<HTMLDivElement>(null);

  const touchStateRef = useRef<TouchState | null>(null);
  const lastTapTimeRef = useRef(0);

  const panXRef = useRef(panX);
  const panYRef = useRef(panY);

  useEffect(() => {
    panXRef.current = panX;
  }, [panX]);

  useEffect(() => {
    panYRef.current = panY;
  }, [panY]);

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setHasReachedLastPage(false);
      setScale(1.0);
      setPanX(0);
      setPanY(0);
    }
  }, [open]);

  useEffect(() => {
    if (currentPage === totalPages && totalPages > 0) {
      setHasReachedLastPage(true);
    }
  }, [currentPage, totalPages]);

  const getActiveContainer = () => pageContainerRef.current;

  const clampPan = (x: number, y: number, s: number) => {
    const container = getActiveContainer();

    const width = container?.clientWidth || mobilePageWidth || screenSize.width;
    const height = container?.clientHeight || Math.round(width * 1.414);

    const maxX = Math.max(0, (width * (s - 1)) / 2);
    const maxY = Math.max(0, (height * (s - 1)) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  useEffect(() => {
    if (isMobile) return;

    const el = pageContainerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
      setScale((prev) => Math.min(SCALE_MAX, Math.max(SCALE_MIN, +(prev + delta).toFixed(2))));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, isMobile]);

  const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    if (numPages === 1) setHasReachedLastPage(true);
  };

  const handleNext = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const center = getTouchCenter(e.touches);
      touchStateRef.current = {
        type: 'pinch',
        startX: 0,
        startY: 0,
        startDist: getTouchDistance(e.touches),
        startScale: scale,
        startPanX: panX,
        startPanY: panY,
        startCenterX: center.x,
        startCenterY: center.y,
      };
      return;
    }

    e.stopPropagation();

    if (e.touches.length === 1) {
      const now = Date.now();

      if (now - lastTapTimeRef.current < DOUBLE_TAP_MS) {
        lastTapTimeRef.current = 0;
        setScale(1.0);
        setPanX(0);
        setPanY(0);
        touchStateRef.current = null;
        return;
      }

      lastTapTimeRef.current = now;

      touchStateRef.current = {
        type: 'single',
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startDist: 0,
        startScale: scale,
        startPanX: panX,
        startPanY: panY,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const state = touchStateRef.current;
    if (!state) return;

    if (state.type === 'pinch' && e.touches.length === 2) {
      const currentDist = getTouchDistance(e.touches);
      const currentCenter = getTouchCenter(e.touches);

      const nextScale = Math.min(
        SCALE_MAX,
        Math.max(SCALE_MIN, +(state.startScale * (currentDist / state.startDist)).toFixed(2)),
      );

      const scaleRatio = nextScale / state.startScale;
      const centerMoveX = currentCenter.x - (state.startCenterX ?? currentCenter.x);
      const centerMoveY = currentCenter.y - (state.startCenterY ?? currentCenter.y);

      const nextPanX = state.startPanX * scaleRatio + centerMoveX;
      const nextPanY = state.startPanY * scaleRatio + centerMoveY;

      const clamped = clampPan(nextPanX, nextPanY, nextScale);

      setScale(nextScale);
      setPanX(clamped.x);
      setPanY(clamped.y);
      return;
    }

    if (state.type === 'single' && scale > 1.0 && e.touches.length === 1) {
      e.stopPropagation();

      const dx = e.touches[0].clientX - state.startX;
      const dy = e.touches[0].clientY - state.startY;

      const clamped = clampPan(state.startPanX + dx, state.startPanY + dy, scale);

      setPanX(clamped.x);
      setPanY(clamped.y);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const state = touchStateRef.current;

    if (!state) return;

    if (state.type === 'single' && scale === 1.0 && e.changedTouches.length === 1) {
      const dx = e.changedTouches[0].clientX - state.startX;
      const dy = e.changedTouches[0].clientY - state.startY;

      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) handleNext();
        else handlePrev();
      }
    }

    touchStateRef.current = null;
  };

  const handleZoomIn = () => setScale((prev) => Math.min(+(prev + SCALE_STEP).toFixed(2), SCALE_MAX));

  const handleZoomOut = () => {
    setScale((prev) => {
      const nextScale = Math.max(+(prev - SCALE_STEP).toFixed(2), SCALE_MIN);

      const clamped = clampPan(panXRef.current, panYRef.current, nextScale);
      setPanX(clamped.x);
      setPanY(clamped.y);

      return nextScale;
    });
  };

  const handleZoomReset = () => {
    setScale(1.0);
    setPanX(0);
    setPanY(0);
  };

  const handlePageClick = () => {
    if (isMobile) return;

    if (scale >= SCALE_MAX) handleZoomOut();
    else handleZoomIn();
  };

  const handleSend = async () => {
    setLoadingSend(true);

    try {
      await onSend();
    } finally {
      setLoadingSend(false);
    }
  };

  const sendDisabled = !hasReachedLastPage;

  const navButtonSx = {
    background: '#fff',
    border: '1px solid #ddd',
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    '&:hover': { background: '#f5f5f5' },
    '&.Mui-disabled': { opacity: 0.35 },
  };

  const pageContainerBaseSx = {
    overflow: isMobile ? 'hidden' : 'auto',
    boxShadow: '0 4px 24px rgba(0,0,0,0.14)',
    borderRadius: 1,
    lineHeight: 0,
    touchAction: isMobile ? 'none' : 'auto',
    cursor: !isMobile && isHoveringPage ? (scale >= SCALE_MAX ? 'zoom-out' : 'zoom-in') : 'default',
  };

  const mobilePanZoomStyle: React.CSSProperties | undefined = isMobile
    ? {
        transform: `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`,
        transformOrigin: 'center center',
        willChange: 'transform',
      }
    : undefined;

  const renderPDFViewer = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        ...(!isMobile && { flex: 1, minHeight: 0 }),
      }}
    >
      {isMobile ? (
        <Box
          ref={pageContainerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{ ...pageContainerBaseSx, width: '100%', borderRadius: 0 }}
        >
          <div style={mobilePanZoomStyle}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
              >
                <Document file={pdfUrl} onLoadSuccess={handleDocumentLoad} loading="Loading document...">
                  <Page
                    pageNumber={currentPage}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={1.0}
                    width={mobilePageWidth}
                  />
                </Document>
              </motion.div>
            </AnimatePresence>
          </div>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            maxWidth: 'calc(100% - 112px)',
          }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={currentPage === 1}
            sx={{
              position: 'absolute',
              right: 'calc(100% + 16px)',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              ...navButtonSx,
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <Box
            ref={pageContainerRef}
            onMouseEnter={() => setIsHoveringPage(true)}
            onMouseLeave={() => setIsHoveringPage(false)}
            onClick={handlePageClick}
            sx={{ ...pageContainerBaseSx, height: '100%' }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
              >
                <Document file={pdfUrl} onLoadSuccess={handleDocumentLoad} loading="Loading document...">
                  <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false} scale={scale} />
                </Document>
              </motion.div>
            </AnimatePresence>
          </Box>

          <IconButton
            onClick={handleNext}
            disabled={currentPage === totalPages || totalPages === 0}
            sx={{
              position: 'absolute',
              left: 'calc(100% + 16px)',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              ...navButtonSx,
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrev} disabled={currentPage === 1} sx={navButtonSx}>
              <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton onClick={handleNext} disabled={currentPage === totalPages || totalPages === 0} sx={navButtonSx}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          {totalPages > 1 && (
            <Typography variant="caption" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <ZoomToolbar scale={scale} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onReset={handleZoomReset} />

          {totalPages > 1 && (
            <Typography variant="caption" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            overflowY: 'auto',
          },
        }}
        PaperProps={{
          sx: {
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            m: 0,
            borderRadius: 0,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
          Review Oral Health Report
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
            minHeight: 0,
            ...(isMobile && { px: 0 }),
          }}
        >
          <div className={styles.renderContainer}>{renderPDFViewer()}</div>

          <Box mt={3} display="flex" flexDirection="column" alignItems="center" gap={2} flexShrink={0}>
            <Button
              variant="contained"
              size="large"
              disabled={sendDisabled || loadingSend}
              onClick={handleSend}
              sx={{ borderRadius: 999, width: 300 }}
            >
              Send to patient
            </Button>

            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OHRReviewDialog;
